import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import type { Tenant } from "@/types/tenant";
import toast from "react-hot-toast";
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbToggleLeft,
  TbToggleRight,
  TbSearch,
} from "react-icons/tb";

export const PlatformTenantList: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await TenantService.listAllTenants();
      setTenants(data);
    } catch (error) {
      console.error("Error loading tenants:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleToggleActive = async (tenant: Tenant) => {
    try {
      await TenantService.updateTenant(tenant.id, {
        is_active: !tenant.is_active,
      });
      toast.success(`Tenant ${tenant.is_active ? "deactivated" : "activated"}`);
      await loadTenants();
    } catch {
      toast.error("Failed to update tenant");
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (
      !confirm(
        `Are you sure you want to permanently delete "${tenant.name}"? This action cannot be undone.`
      )
    )
      return;

    try {
      await TenantService.deleteTenant(tenant.id);
      toast.success("Tenant permanently deleted");
      await loadTenants();
    } catch {
      toast.error("Failed to delete tenant");
    }
  };

  const filtered = tenants.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-[#444]">Tenants</h2>
        <button
          onClick={() => navigate("/platform/tenants/new")}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#374795] rounded-lg hover:bg-[#1c398e] transition-colors cursor-pointer"
        >
          <TbPlus className="w-4 h-4" /> New Tenant
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <TbSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#828282]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tenants by name or slug..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#374795] focus:border-transparent"
        />
      </div>

      {/* Tenant list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#828282] text-sm">
          {search
            ? "No tenants match your search."
            : "No tenants yet. Create your first tenant to get started."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((tenant) => {
            const memberCount = Array.isArray(tenant.tenant_members)
              ? tenant.tenant_members[0]?.count || 0
              : 0;
            const settings = Array.isArray(tenant.tenant_settings)
              ? tenant.tenant_settings[0]
              : tenant.tenant_settings;

            return (
              <div
                key={tenant.id}
                className={`flex items-center justify-between p-4 rounded-lg border shadow-sm ${
                  tenant.is_active
                    ? "border-gray-200 bg-white"
                    : "border-red-200 bg-red-50"
                }`}
              >
                <div
                  className="flex items-center gap-4 cursor-pointer flex-1 min-w-0"
                  onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                >
                  {settings?.logo_url ? (
                    <img
                      src={settings.logo_url}
                      alt={tenant.name}
                      className="w-10 h-10 rounded-lg object-contain bg-gray-50 shrink-0"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
                      style={{
                        backgroundColor: settings?.primary_color || "#374795",
                      }}
                    >
                      {tenant.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#444]">
                      {tenant.name}
                      {!tenant.is_active && (
                        <span className="ml-2 text-xs font-medium text-red-500">
                          (Inactive)
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-[#828282] truncate">
                      {settings?.subdomain || tenant.slug}.taskrsol.com
                      {memberCount > 0 && ` · ${memberCount} members`}
                      {settings?.plan_tier && ` · ${settings.plan_tier}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                    className="p-2.5 text-[#828282] hover:text-[#374795] hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                    title="Configure tenant"
                  >
                    <TbEdit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(tenant)}
                    className="p-2.5 text-[#828282] hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors cursor-pointer"
                    title={tenant.is_active ? "Deactivate" : "Activate"}
                  >
                    {tenant.is_active ? (
                      <TbToggleRight className="w-5 h-5 text-green-500" />
                    ) : (
                      <TbToggleLeft className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteTenant(tenant)}
                    className="p-2.5 text-[#828282] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Delete tenant"
                  >
                    <TbTrash className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
